import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { AnimatedText, SlideUpText, GoldUnderline, AnimatedCounter } from './TextAnimations';
import { useLanguage } from '../context/LanguageContext';

const Hero = () => {
  const [loaded, setLoaded] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const { t } = useLanguage();
  
  useEffect(() => {
    setLoaded(true);
  }, []);

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToProjects = () => {
    document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="home"
      className="relative min-h-screen flex flex-col justify-center items-center overflow-hidden bg-[#0A0A0A]"
    >
      {/* Video Background */}
      <div className="absolute inset-0 z-0">
        {/* Fallback Image - always visible as base layer */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1920&q=80)',
          }}
        />
        
        {/* Video Element - Using client's painting video */}
        <video
          autoPlay
          muted
          loop
          playsInline
          onLoadedData={() => setVideoLoaded(true)}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${videoLoaded ? 'opacity-100' : 'opacity-0'}`}
          poster="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1920&q=80"
        >
          <source src="https://customer-assets.emergentagent.com/job_734cb631-c213-4223-9ec5-3c4d6675302f/artifacts/zvk88js0_85340-590746448_small.mp4" type="video/mp4" />
        </video>
        
        {/* Dark Overlay - consistent across all devices */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70" />
        <div className="absolute inset-0 bg-black/40" />
        
        {/* Subtle Noise Texture */}
        <div 
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          }}
        />
        
        {/* Gold accent line at bottom */}
        <motion.div 
          className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#C5A55C]/60 to-transparent"
          initial={{ scaleX: 0 }}
          animate={loaded ? { scaleX: 1 } : {}}
          transition={{ delay: 1.5, duration: 1.5, ease: [0.77, 0, 0.175, 1] }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-6 text-center pt-24 lg:pt-28">
        {/* Logo */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={loaded ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 1.2, ease: 'easeOut' }}
          className="mb-16"
        >
          <img
            src="https://customer-assets.emergentagent.com/job_734cb631-c213-4223-9ec5-3c4d6675302f/artifacts/573hpmih_Transparent-PNG.png"
            alt="Martinez Solutions"
            className="h-28 md:h-36 lg:h-44 mx-auto"
          />
        </motion.div>

        {/* Main Headline with Character Animation */}
        <div className="mb-4">
          <h1 className="font-montserrat font-extralight text-4xl md:text-6xl lg:text-7xl text-white tracking-[0.2em] uppercase">
            <AnimatedText text={t('hero.title1')} delay={0.4} staggerDelay={0.04} />
          </h1>
        </div>
        
        <div className="mb-10">
          <h1 className="font-montserrat font-extralight text-4xl md:text-6xl lg:text-7xl text-[#C5A55C] tracking-[0.2em] uppercase">
            <AnimatedText text={t('hero.title2')} delay={0.8} staggerDelay={0.04} />
          </h1>
        </div>

        {/* Gold Line Separator */}
        <div className="w-24 mx-auto mb-10">
          <GoldUnderline delay={1.4} />
        </div>

        {/* Subtitle */}
        <SlideUpText delay={1.2} className="mb-16">
          <p className="font-raleway font-light text-lg md:text-xl text-white/50 tracking-[0.15em] max-w-2xl mx-auto">
            {t('hero.subtitle')}
          </p>
        </SlideUpText>

        {/* CTA Buttons */}
        <SlideUpText delay={1.4}>
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <motion.button
              onClick={scrollToContact}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="group px-12 py-5 border border-[#C5A55C] text-[#C5A55C] bg-transparent hover:bg-[#C5A55C] hover:text-black transition-all duration-500 font-montserrat font-light text-sm tracking-[0.2em] uppercase"
              data-testid="hero-cta-primary"
              data-hover
            >
              <span className="flex items-center gap-3">
                {t('hero.cta1')}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
              </span>
            </motion.button>

            <motion.button
              onClick={scrollToProjects}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="px-12 py-5 border-b border-white/30 text-white hover:border-[#C5A55C] hover:text-[#C5A55C] transition-all duration-300 bg-transparent font-montserrat font-light text-sm tracking-[0.2em] uppercase"
              data-testid="hero-cta-secondary"
              data-hover
            >
              {t('hero.cta2')}
            </motion.button>
          </div>
        </SlideUpText>

        {/* Trust Indicators with Counter Animation */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={loaded ? { opacity: 1 } : {}}
          transition={{ delay: 2, duration: 1 }}
          className="mt-32 flex justify-center items-center gap-12 md:gap-20"
        >
          <TrustIndicator value="500" suffix="+" label={t('hero.stat1Label')} delay={2.2} />
          <div className="w-[1px] h-12 bg-white/10" />
          <TrustIndicator value="15" suffix="+" label={t('hero.stat2Label')} delay={2.4} />
          <div className="w-[1px] h-12 bg-white/10" />
          <TrustIndicator value="100" suffix="%" label={t('hero.stat3Label')} delay={2.6} />
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={loaded ? { opacity: 1 } : {}}
        transition={{ delay: 2.5, duration: 1 }}
        className="absolute bottom-12 left-1/2 transform -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
          className="flex flex-col items-center gap-2"
        >
          <span className="font-montserrat font-light text-[10px] tracking-[0.3em] text-white/30 uppercase">{t('hero.scroll')}</span>
          <motion.div 
            className="w-[1px] h-8 bg-gradient-to-b from-[#C5A55C] to-transparent"
            initial={{ scaleY: 0 }}
            animate={loaded ? { scaleY: 1 } : {}}
            transition={{ delay: 2.8, duration: 0.8, ease: 'easeOut' }}
            style={{ transformOrigin: 'top' }}
          />
        </motion.div>
      </motion.div>
    </section>
  );
};

const TrustIndicator = ({ value, suffix, label, delay }) => (
  <div className="text-center">
    <div className="font-montserrat font-extralight text-3xl md:text-4xl text-white tracking-wider mb-2">
      <AnimatedCounter value={value} suffix={suffix} delay={delay} duration={2} />
    </div>
    <div className="font-raleway font-light text-[10px] text-white/40 tracking-[0.25em] uppercase">{label}</div>
  </div>
);

export default Hero;
